import { useAuth0 } from "@auth0/auth0-react";
import CircularProgress from "@mui/material/CircularProgress";
import MembersSettingService from "Lib/API/MemberSetting/MemeberSetting";
import DefaultEmptyMsg from 'components/DefaultEmptyMsg/DefaultEmptyMsg';
import AlertDialog from "components/Dialog/index";
import Button from 'components/button/Button';
import SideMenuList from 'components/sideMenuList/SideMenuList';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toastr } from "react-redux-toastr";
import { getOrganizationInfoFromLocalStorage } from "utils/functions";
import SettingHeader from '../../SettingHeader/settingHEader';
import DropdownOptionForm from './DropdownOptionForm';
import { DropdownOptionsActions, DropdownOptionsContainer, DropdownOptionsContent, DropdownOptionsContentForm, DropdownOptionsHeader, DropdownOptionsTitle } from './Theme';

const DropDownOptions = ({ heading }) => {
  const organization = getOrganizationInfoFromLocalStorage(); 
  const { getAccessTokenSilently } = useAuth0();

  const [selectedDropdownOption, setSelectedDropdownOption] = useState(null);
  const [viewType, setViewType] = useState("view");
  const [editLoading, setEditLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null,
    isLoading: false,
    text: ""
  });
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const getDropdownOptionsHandler = useCallback(async () => {
    const token = await getAccessTokenSilently();
    MembersSettingService.getDropdownOptions({token})
    .then((response) => {
      const result = response?.result;
      const dropdownOptions = result.map((dropdown) => {
        return {
          label: dropdown?.title,
          value: dropdown?.title,
          options: dropdown?.options,
          hidden: dropdown?.hidden
        }
      });
      setDropdownOptions(dropdownOptions);
    })
    .catch((err) => {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    })
    .finally(() => {
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    getDropdownOptionsHandler();
  }, []);

  const onDropdownOptionChange = (option) => {
    const updatedOptions = selectedDropdownOption?.options.map((item) => {
      if(item.value === option.value) {
        return {
          ...item,
          checked: !item.checked
        }
      }
      return item;
    });
    setSelectedDropdownOption((prev) => ({
      ...prev,
      options: updatedOptions
    }));
  }

  const onDropdownHiddenChange = (checked) => {
    setSelectedDropdownOption((prev) => ({
      ...prev,
      hidden: checked
    }));
  }

  const checkValidation = () => {
    const isValid = selectedDropdownOption?.options.some((option) => option.checked);
    if(!isValid) {
      toastr.info("Validation", "At least one option should be checked");
    }
    return isValid;
  }

  const updateDropdownOptions = async () => {
    const isValid = checkValidation();
    if(!isValid) return;
    setEditLoading(true);
    const token = await getAccessTokenSilently();
    const options = selectedDropdownOption?.options.filter((option) => option.checked).map((option) => {
      return {
        label: option.label,
        value: option.value
      }
    });
    const payload = {
      title: selectedDropdownOption?.value,
      hidden: selectedDropdownOption?.hidden,
      options,
    }
    MembersSettingService.updateDropdownOptions({token, data:payload}) 
    .then((response) => {
      toastr.success("Success", "Dropdown options updated successfully");
      getDropdownOptionsHandler();
       const updatedOrganization = {
        ...organization,
        generalOptions: response?.result?.generalOptions,
      }
      localStorage.setItem("organization", JSON.stringify(updatedOrganization));
      const generalOptions = response?.result?.generalOptions;
      const generalOptionsObj = generalOptions.reduce((acc, item) => {
        acc[item.title] = {
          hidden: item.hidden,
          options: item.options
        };
        return acc;
      }, {});
      localStorage.setItem("generalOptions", JSON.stringify(generalOptionsObj));
      changeViewType("view");
    })
    .catch((err) => {
      toastr.error("Error", err?.data?.message || "Something went wrong");
    })
    .finally(() => {
      setEditLoading(false);
    });
  }

  const changeViewType = (type) => {
    setViewType(type);
  }

  const onDropdownOptionSelect = (dropdownOption) => {
    if(viewType === "edit") {
      cancelEditDropdownOption();
      return;
    }
    setSelectedDropdownOption(dropdownOption);
  }

  const onEditDropdownOption = () => {
    changeViewType("edit");
  }

  const cancelEditDropdownOption = () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        open: true,
        type: "discard",
        text: "Do you want to discard the changes you made?"
      }
    ));
  }

  const closeConfirmModal = () => {
    setConfirmModal((prev) => (
      {
        ...prev,
        open: false,
        type: null,
        text: "",
        isLoading: false
      }
    ));
  }

  const onConfirmModalAccept = () => {
    if (confirmModal.type === "discard") {
      const original = dropdownOptions.find(opt => opt.value === selectedDropdownOption?.value);
      if (original) setSelectedDropdownOption(original);
      changeViewType("view");
    }
    closeConfirmModal();
  }

  const isDisabled = useMemo(() => {
    return editLoading;
  }, [editLoading]);

  return (
    <div className="w-100">
      <SettingHeader
        heading={heading}
        edit={false}
        addNew={false}
        path={"/financial-setting/members/dropdown-options"}
        parentHeading={"Members"}
        alertOnEdit={false}
      />
      <div className="main-setting__wrapper">
        <div className="setting-main">
          <div className="left-menu-div">
            <SideMenuList
              list={dropdownOptions}
              selected={selectedDropdownOption?.value}
              onClick={(dropdownOption) => onDropdownOptionSelect(dropdownOption)}
            />
          </div>
          <DropdownOptionsContainer className="settings-details">
            {selectedDropdownOption?.value ? (
              <>
                <DropdownOptionsHeader>
                  <DropdownOptionsTitle>
                    {selectedDropdownOption?.label}
                  </DropdownOptionsTitle>
                  <DropdownOptionsActions>
                    {viewType === "view" && (
                      <>
                        <Button
                          size='sm'
                          variant='transparent'
                          style={{
                            color: "#2555EF",
                            padding: "0",
                            fontWeight: "400",
                            fontSize: "14px"
                          }}
                          onClick={() => onEditDropdownOption()}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            class="bi bi-pencil-square"
                            viewBox="0 0 16 16"
                          >
                            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                            <path
                              fill-rule="evenodd"
                              d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"
                            />
                          </svg>
                          <span>Edit</span>
                        </Button>
                      </>
                    )}
                    {viewType !== "view" && (
                      <>
                        <Button
                          size="md"
                          variant="primary-outline"
                          onClick={() => cancelEditDropdownOption()}
                          style={{ minWidth: 80 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="md"
                          variant="primary"
                          onClick={() => {
                            if (isDisabled) return;
                            updateDropdownOptions();
                          }}
                          style={{ minWidth: 80 }}
                          disabled={isDisabled}
                        >
                          {editLoading ? (
                            <div className="d-flex">
                              <CircularProgress
                                size={22}
                                style={{
                                  color: "#ffffff",
                                }}
                              />
                            </div>
                          ) : "Save"}
                        </Button>
                      </>
                    )}
                  </DropdownOptionsActions>
                </DropdownOptionsHeader>
                <DropdownOptionsContent>
                  <DropdownOptionsContentForm
                    disabled={viewType === "view"}
                  >
                    <DropdownOptionForm
                      disabled={viewType === "view"}
                      dropdownData={selectedDropdownOption}
                      onChange={onDropdownOptionChange}
                      onDropdownHiddenChange={onDropdownHiddenChange}
                    />
                  </DropdownOptionsContentForm>
                </DropdownOptionsContent>
              </>
            ) : (
              <div className="mt-40 ledgers-settings-empty">
                <DefaultEmptyMsg msg="No Dropdown option selected" size="30px" />
              </div>
            )}
          </DropdownOptionsContainer>
        </div>
      </div>
      <AlertDialog
        open={confirmModal.open}
        onDecline={closeConfirmModal}
        onAccept={onConfirmModalAccept}
        title={"Are you sure?"}
        description={confirmModal.text}
      />
    </div>
  )
}

export default DropDownOptions